import {
  getFolders,
  getRecentFolders,
  getArchivedFolders,
  getFolderResources,
  getSharedFolder,
  deleteFolderResource,
  addCollectionContent,
  addGoalToFolder,
  addStrategyToFolder,
  addFolder,
  getAllResourcesForContentTypes,
  getCollectionNewContributions,
  fetchFolderComments,
  createComment,
  deleteComment
} from './api.ts'
import { defineStore } from 'pinia'
import { LibraryFolder, LibraryFolders, LibraryContentObject, LibraryFolderResponse, ErrorResponse, ContributorIdsForContentMap, FolderComment } from '@/library/library_types'

import { CONTENT_TYPES } from '@/util/access_visibility'
import { useUserStore } from '@/authentication/user.store'
import { EditDocument, RemoveContent } from '@/library/library_types.ts'
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store.ts'
import { isFolderShared, getFolderOwnerUuid } from '@/library/library_helpers.ts'

export const useLibraryStore = defineStore('library', {
  state: () => ({
    folders: [] as LibraryFolder[],
    recentFolders: [] as LibraryFolder[],
    archivedFolders: [] as LibraryFolder[],
    loadingFolders: true,

    currentFolder: {} as LibraryFolder,
    currentFolderPermissions: [] as string[],
    currentFolderComments: [] as FolderComment[],
    loadingResources: true,
    folderUuidsByContentUuid: {} as Record<string, string>,
    goals: [] as LibraryContentObject[],
    strategies: [] as LibraryContentObject[],
    presentlevels: [] as LibraryContentObject[],
    presentlevelStudentSummaries: [] as LibraryContentObject[],
    combinedPresentLevels: [] as LibraryContentObject[],
    impactsOfDisabilities: [] as LibraryContentObject[],
    resources: [] as LibraryContentObject[],
    attachments: [] as LibraryContentObject[],
    loadFolderError: false,
    loadFolderErrorStatusCode: 0,
    loadFoldersError: false,
    deleteResourceError: false,

    filters: {
      goals: 'All Subjects',
      strategies: 'All Subjects',
      presentlevel: 'All Subjects'
    },

    newContributions: {} as Record<string, string[]>
  }),
  actions: {
    removeResource(resourceData: { uuid: string, type: string, collection: string }) {
      switch (resourceData.type) {
        case CONTENT_TYPES.SAVED_STRATEGY:
          this.strategies = this.strategies.filter((strategy) => {
            return strategy.content_uuid !== resourceData.uuid
          })
          break
        case CONTENT_TYPES.ATTACHMENT_LINK:
        case CONTENT_TYPES.ATTACHMENT_FILE:
          this.attachments = this.attachments.filter((a) => {
            return a.content_uuid !== resourceData.uuid
          })
          break
        case CONTENT_TYPES.SAVED_GOAL:
        case CONTENT_TYPES.SAVED_SUPER_PAGE:
          this.goals = this.goals.filter((goal) => {
            return goal.content_uuid !== resourceData.uuid
          })
          break
        case CONTENT_TYPES.PRESENTLEVEL:
          this.presentlevels = this.presentlevels.filter((pl) => {
            return pl.content_uuid !== resourceData.uuid
          })
          this.combinedPresentLevels = this.presentlevels.concat(this.presentlevelStudentSummaries)
          break
        case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
          this.presentlevelStudentSummaries = this.presentlevelStudentSummaries.filter((pl) => {
            return pl.content_uuid !== resourceData.uuid
          })
          this.combinedPresentLevels = this.presentlevels.concat(this.presentlevelStudentSummaries)
          break
        case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
          this.impactsOfDisabilities = this.impactsOfDisabilities.filter((iod) => {
            return iod.content_uuid !== resourceData.uuid
          })
          break
        case CONTENT_TYPES.RESOURCE:
          this.resources = this.resources.filter((r) => {
            return r.content_uuid !== resourceData.uuid
          })
          break
        default:
          break
      }
    },
    setResourcesForFolder(collection: LibraryFolder) {
      this.currentFolder = collection
      const goals = [] as LibraryContentObject[]
      const strategies = [] as LibraryContentObject[]
      const presentlevels = [] as LibraryContentObject[]
      const presentlevelStudentSummaries = [] as LibraryContentObject[]
      const impactsOfDisabilities = [] as LibraryContentObject[]
      const resources = [] as LibraryContentObject[]
      const attachments = [] as LibraryContentObject[]

      if (collection.content) {
        collection.content.forEach(function (resource: LibraryContentObject) {
          if (resource.content_body_error === 'not found' || !resource.content_body) {
            return
          }
          if (
            resource.content_type === CONTENT_TYPES.SAVED_GOAL ||
            resource.content_type === CONTENT_TYPES.SAVED_SUPER_PAGE
          ) {
            goals.push(resource)
          } else if (resource.content_type === CONTENT_TYPES.SAVED_STRATEGY) {
            strategies.push(resource)
          } else if (resource.content_type === CONTENT_TYPES.PRESENTLEVEL) {
            presentlevels.push(resource)
          } else if (resource.content_type === CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY) {
            presentlevelStudentSummaries.push(resource)
          } else if (resource.content_type === CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY) {
            impactsOfDisabilities.push(resource)
          } else if (resource.content_type === CONTENT_TYPES.RESOURCE) {
            resources.push(resource)
          } else if (resource.content_type === CONTENT_TYPES.ATTACHMENT_LINK || resource.content_type === CONTENT_TYPES.ATTACHMENT_FILE) {
            attachments.push(resource)
          }
        })
      }

      this.strategies = this.sortContentsByCreatedAt(strategies)
      this.goals = this.sortContentsByCreatedAt(goals)
      this.combinedPresentLevels = this.sortContentsByCreatedAt(presentlevels.concat(presentlevelStudentSummaries))
      this.presentlevels = this.sortContentsByCreatedAt(presentlevels)
      this.presentlevelStudentSummaries = this.sortContentsByCreatedAt(presentlevelStudentSummaries)
      this.impactsOfDisabilities = this.sortContentsByCreatedAt(impactsOfDisabilities)
      this.resources = this.sortContentsByCreatedAt(resources)
      this.attachments = this.sortContentsByCreatedAt(attachments)

      this.calculateNewContributions(collection)
    },
    calculateNewContributions(collection: LibraryFolder) {
      const userStore = useUserStore()
      if (!userStore?.userId) {
        return
      }
      const ownerUuid = getFolderOwnerUuid(collection)
      const isOwner = ownerUuid === userStore.userId

      if (!isOwner || !collection.uuid || !collection.content) {
        return
      }

      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
      const newContentUuids: string[] = []

      collection.content.forEach((item: LibraryContentObject) => {
        if (!item.created_at) {
          return
        }
        if (item.contributor === ownerUuid) {
          return
        }
        const createdAt = new Date(item.created_at)
        if (createdAt > fourteenDaysAgo) {
          newContentUuids.push(item.content_uuid)
        }
      })

      this.newContributions[collection.uuid] = newContentUuids
    },
    sortContentsByCreatedAt(contents: LibraryContentObject[]) {
      return contents.sort((a, b) => {
        if (!a.created_at) {
          return 0
        }
        if (!b.created_at) {
          return -1
        }
        if (a.created_at < b.created_at) {
          return 1
        }
        if (a.created_at > b.created_at) {
          return -1
        }
        return 0
      })
    },
    async fetchFolders(userId: string) {
      this.loadingFolders = true
      this.loadFoldersError = false
      try {
        const data = await getFolders(userId) as LibraryFolders;
        if (!data?.collections) {
          console.error('Failed to fetch folders:', data)
          this.loadFoldersError = true
          this.folders = [];
        } else {
          this.folders = data.collections;
          this.loadFoldersError = false
        }
      } catch (error) {
        console.error('Failed to fetch folders:', error)
        this.loadFoldersError = true
        this.folders = []
      } finally {
        this.loadingFolders = false
      }
    },
    async fetchRecentFolders(userId: string) {
      const data = await getRecentFolders(userId)
      this.recentFolders = data
    },
    async createFolder(folderName: string, userId: string) {
      this.loadingFolders = true
      try {
        const newFolder = await addFolder(folderName, userId)
        if (!this.folders.find(f => f.uuid === newFolder.uuid)) {
          this.folders.push(newFolder)
        }
        if (!this.recentFolders.find(f => f.uuid === newFolder.uuid)) {
          this.recentFolders.unshift(newFolder)
        }
        return newFolder
      } catch (error) {
        console.error('Failed to create folder:', error)
        throw error
      } finally {
        this.loadingFolders = false
      }
    },
    async fetchArchivedFolders(userId: string) {
      this.loadingFolders = true
      this.loadFoldersError = false
      try {
        const data = await getArchivedFolders(userId)
        if (data?.collections) {
          this.archivedFolders = data.collections
        } else {
          this.loadFoldersError = true
        }
      } catch (error) {
        console.error('Failed to fetch archived folders:', error)
        this.loadFoldersError = true
      } finally {
        this.loadingFolders = false
      }
    },
    async fetchResourcesForFolder({ folderUUID, params }: { folderUUID: string, params: object }) {
      this.loadingResources = true
      this.loadFolderError = false
      this.loadFolderErrorStatusCode = 0

      try {
        const data = await getFolderResources(folderUUID, params)
        if (!data?.collection) {
          console.error('Failed to fetch folder resources:', data)
          this.loadFolderError = true
          this.loadFolderErrorStatusCode = 500
          return
        }
        this.setResourcesForFolder(data.collection)
        if (data.permissions) {
          this.setPermissions(data.permissions)
        }

        this.fetchNewContributionsForFolder(folderUUID, data.collection)

        this.loadFolderError = false
        this.loadFolderErrorStatusCode = 0
      } catch (error) {
        this.loadFolderError = true
        this.loadFolderErrorStatusCode = (error as ErrorResponse)?.status_code
      } finally {
        this.loadingResources = false
      }
    },
    async fetchNewContributionsForFolder(folderUUID: string, collection: LibraryFolder) {
      const userStore = useUserStore()
      const ownerUuid = getFolderOwnerUuid(collection)
      const isOwner = ownerUuid === userStore.userId

      if (!isOwner) {
        return
      }

      try {
        const newContributionsResponse = await getCollectionNewContributions(folderUUID)
        if (newContributionsResponse?.new_content_uuids) {
          this.newContributions[folderUUID] = newContributionsResponse.new_content_uuids
        }
      } catch (error) {
        console.error('Failed to fetch new contributions:', error)
      }
    },
    async fetchResourcesForSharedFolder({ shareToken, params }: { shareToken: string, params: object }) {
      this.loadingResources = true
      try {
        const data = await getSharedFolder(shareToken, params) as unknown
        if (data && (data as LibraryFolderResponse)?.collection) {
          this.setResourcesForFolder((data as LibraryFolderResponse).collection)
          this.loadFolderError = false
        } else {
          this.loadFolderError = true
        }
      } catch (error) {
        console.error('Failed to fetch shared folder:', error)
        this.loadFolderError = true
      }
      this.loadingResources = false
    },
    getFolderByContentUuid(contentUuid: string): LibraryFolder | undefined {
      const folderUuid = this.folderUuidsByContentUuid[contentUuid]
      const folder = this.folders.find(folder => folder.uuid === folderUuid)
      if (folder) {
        return folder
      }
      return undefined
    },
    storeLibraryContent(contentType: string, content: LibraryContentObject) {
      switch (contentType) {
        case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
          this.presentlevelStudentSummaries.push(content)
          break;
        default:
          break;
      }
    },
    async fetchFoldersForContentByType(
      contentType: string,
      includeShared: boolean = true,
      persistenceFilter: (content: LibraryContentObject) => boolean = () => true)
    {
      try {
        const userStore = useUserStore()
        await this.fetchFolders(userStore.userId)
        const response = await getAllResourcesForContentTypes(userStore.userId, [contentType], includeShared)
        const contentItems = response.content || []
        const updatedFolderContentMap: Record<string, string> = {}
        contentItems.forEach(contentItem => {
          if (contentItem?.content_uuid && contentItem?.collection_uuid) {
            const libraryFolder = this.folders.find(folder => folder.uuid === contentItem?.collection_uuid)
            if (libraryFolder && contentItem?.content_uuid && persistenceFilter(contentItem)) {
              updatedFolderContentMap[contentItem.content_uuid] = libraryFolder.uuid
              this.storeLibraryContent(contentType, contentItem)
            }
          }
        })
        this.folderUuidsByContentUuid = updatedFolderContentMap
      } catch (e) {
        console.error(`Failed to fetch folders for ${contentType}:`, e)
        throw e
      }
    },
    async fetchFoldersForPLSubjectSummaries() {
      const summaryUUIDs = usePresentLevelsStore().subjectSummaries.map(summary => summary.uuid)
      const summaryUUIDfilter = (librarySummary: LibraryContentObject) => summaryUUIDs.includes(librarySummary.content_uuid)
      await this.fetchFoldersForContentByType(CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY, true, summaryUUIDfilter)
    },
    async deleteResource(resourceData: { uuid: string, type: string, collection: string }) {
      this.deleteResourceError = false
      try {
        await deleteFolderResource(resourceData.collection, resourceData.uuid, resourceData.type)
        this.removeResource(resourceData)
      } catch (error) {
        console.error('Failed to delete resource:', error)
        this.deleteResourceError = true
      }
      this.removeResource(resourceData)
    },
    async saveToLibrary(params: {
      contentType: string,
      contentUuid: string,
      collectionUuid: string,
      contributor: string,
      notes?: string,
      folder?: { uuid: string, name: string },
      contentPayload?: Record<string, any>
    }) {
      try {
        let result;

        // Group related content types to determine the handling approach
        const isGoalType = [
          CONTENT_TYPES.SAVED_GOAL,
          CONTENT_TYPES.SAVED_SUPER_PAGE,
          CONTENT_TYPES.GOAL,
          CONTENT_TYPES.SUPER_PAGE
        ].includes(params.contentType);

        const isStrategyType = [
          CONTENT_TYPES.SAVED_STRATEGY,
          CONTENT_TYPES.STRATEGY
        ].includes(params.contentType);

        const isAttachmentType = params.contentType === CONTENT_TYPES.ATTACHMENT_LINK

        switch (true) {
          case isGoalType:
            if (!params.folder) {
              throw new Error('Folder information is required for saving goals')
            }

            result = await addGoalToFolder(
              params.folder,
              params.contentPayload || { content_uuid: params.contentUuid },
            );
            break;

          case isStrategyType:
            if (!params.folder) {
              throw new Error('Folder information is required for saving strategies')
            }

            result = await addStrategyToFolder({
              userUuid: params.contributor,
              strategyUuid: params.contentUuid,
              folderUuid: params.folder.uuid,
              folderName: params.folder.name,
              notes: params.notes,
            });
            break;

          case isAttachmentType:
            result = await addCollectionContent({
              collectionUuid: params.collectionUuid,
              contentType: params.contentType,
              contentUuid: params.contentUuid,
              contributor: params.contributor,
              url: params.contentPayload?.url,
              contentTitle: params.contentPayload?.contentTitle ?? params.contentPayload?.content_title,
            });
            break;

          default:
            // Default case for other content types
            result = await addCollectionContent({
              collectionUuid: params.collectionUuid,
              contentType: params.contentType,
              contentUuid: params.contentUuid,
              contributor: params.contributor,
            });
            break;
        }

        return result;
      } catch (error) {
        console.error('Error saving to library:', error)
        throw error
      }
    },
    setPermissions(permissions: string[]) {
      this.currentFolderPermissions = permissions
    },
    setCurrentFolderByUuid(uuid: string) {
      this.currentFolder = this.folders.find(folder => folder.uuid === uuid) || {} as LibraryFolder
      this.currentFolderPermissions = this.currentFolder.permissions || []
      this.currentFolderComments = []
    },
    async fetchCurrentFolderComments(folderUuid: string) {
      const requestedUuid = folderUuid
      try {
        const response = await fetchFolderComments(folderUuid)
        if (requestedUuid !== this.currentFolderUuid) return
        this.currentFolderComments = response?.comments || []
      } catch (error) {
        if (requestedUuid !== this.currentFolderUuid) return
        console.error('Failed to fetch folder comments:', error)
        this.currentFolderComments = []
      }
    },
    async addCurrentFolderComment(folderUuid: string, content: string): Promise<FolderComment> {
      try {
        const response = await createComment(folderUuid, content)
        // Reload comments after adding to ensure proper ordering and handle concurrent changes
        await this.fetchCurrentFolderComments(folderUuid)
        return response
      } catch (error) {
        console.error('Failed to create comment:', error)
        throw error
      }
    },
    async deleteCurrentFolderComment(folderUuid: string, commentUuid: string) {
      try {
        await deleteComment(folderUuid, commentUuid)
        await this.fetchCurrentFolderComments(folderUuid)
      } catch (error) {
        console.error('Failed to delete comment:', error)
        throw error
      }
    }
   },
  getters: {
    currentFolderContentCount(state): number {
      return (
        state.goals.length +
        state.strategies.length +
        state.presentlevels.length +
        state.impactsOfDisabilities.length +
        state.resources.length
      )
    },
    folderCount(state): number {
      return (state.folders || []).length
    },
    archivedFolderCount(state): number {
      return (state.archivedFolders || []).length
    },
    isCurrentFolderArchived(state): boolean {
      return !!state.currentFolder.archived_at && state.currentFolder.archived_at !== '0001-01-01T00:00:00Z'
    },
    hasAddRemoveContributorPerms(state): boolean {
      return state.currentFolderPermissions.includes('invite_user') && state.currentFolderPermissions.includes('remove_user')
    },
    hasRemoveContentPerms(state): boolean {
      return state.currentFolderPermissions.includes('remove_content')
    },
    userOwnedFolders(state): LibraryFolder[] {
      const userStore = useUserStore()
      return state.folders.filter(folder => getFolderOwnerUuid(folder) === userStore.userId)
    },
    sharedFolders(state): LibraryFolder[] {
      const userStore = useUserStore()
      return state.folders.filter(folder => getFolderOwnerUuid(folder) !== userStore.userId)
    },
    getContributorIdsForContent(state): ContributorIdsForContentMap {
      const folder = state.currentFolder
      const folderOwner = getFolderOwnerUuid(folder)
      if (!folder?.content || !folder.content.length) return {} as ContributorIdsForContentMap
      return folder.content.reduce((acc: ContributorIdsForContentMap, contentObj: LibraryContentObject) => {
        const {content_uuid, contributor} = contentObj
        if (content_uuid && contributor) {
          const isFolderOwner = contributor === folderOwner
          acc[content_uuid] = {contributor, isFolderOwner}
        }
        return acc
      }, {} as ContributorIdsForContentMap)
    },
    currentFolderHasCurrentContributors(state): boolean {
      const roles = state.currentFolder?.folder_roles?.filter(role => role.role.toLowerCase() === 'contributor')
      return roles ? roles.length > 0 : false
    },
    doesUserOwnCurrentFolder(state): boolean {
      const owner = state.currentFolder?.folder_roles?.find(role => role.role === 'owner')
      const userStore = useUserStore()
      return owner?.user_uuid === userStore.userId
    },
    currentFolderUuid(state): string {
      return state.currentFolder?.uuid || ''
    },
    currentFolderOwner(state): string {
      return state.currentFolder?.folder_roles?.find(role => role.role === 'owner')?.user_uuid || ''
    },
    recentUserOwnedFolders: (state) => {
      return state.recentFolders.filter(folder => getFolderOwnerUuid(folder) === useUserStore().userId)
    },
    getNewContentCount: (state) => (folderUuid: string): number => {
      return state.newContributions[folderUuid]?.length || 0
    },
    isContentNew: (state) => (contentUuid: string, folderUuid: string): boolean => {
      return state.newContributions[folderUuid]?.includes(contentUuid) || false
    },
    currentFolderUserRole(state): string {
      const currentUserUuid = useUserStore().userId
      const currentFolder = state.currentFolder?.folder_roles?.find(role => role.user_uuid === currentUserUuid)
      return currentFolder?.role || ''
    },
    getUserRoleForFolder: (state) => (folderUuid: string): string | null => {
      const userStore = useUserStore()
      const currentUserId = userStore.userId

      const folder = state.folders.find(folder => folder.uuid === folderUuid)
      if (!folder || !folder.folder_roles) return null

      const userRole = folder.folder_roles.find(role => role.user_uuid === currentUserId)
      return userRole ? userRole.role : null
    },
    canEditCurrentFolderContent(state): boolean {
      return state.currentFolderPermissions.includes(EditDocument)
    },
    canDeleteCurrentFolderContent(state): boolean {
      return state.currentFolderPermissions.includes(RemoveContent)
    },
    isCurrentFolderShared(state): boolean {
      return isFolderShared(state.currentFolder)
    },
    getCurrentFolderComments(state): FolderComment[] {
      return state.currentFolderComments
    },
    getCurrentFolderCommentCount(state): number {
      return state.currentFolderComments.length
    }
  }
})